// Los campos del contrato de extracción tal como los muestra la pantalla de verificación (diseño M3 §2
// y §4 de fct-ferias): agrupados como los agrupa la fuente §7, con su etiqueta y cómo se edita cada uno.
// Las claves son las del contrato v1 (D-FE-28); una prueba las compara con las de la API.
import type { EstadoCampo, ValorCampo } from '../../api/feriasApi';

/** Cómo se edita un campo. */
export type Forma =
  | 'texto'
  | 'textoLargo'
  | 'opcion'
  | 'si-no'
  | 'casillas'
  | 'etiquetas'
  | 'relaciones';

/** Un campo del contrato en la pantalla. */
export interface Campo {
  clave: string;
  etiqueta: string;
  forma: Forma;
  /** Las etiquetas del contrato, para `opcion` y `casillas`: [valor, lo que muestra la pantalla]. */
  opciones?: [string, string][];
  /** Datos en Roboto Mono: códigos, teléfonos, fechas (manual, regla 6). */
  mono?: boolean;
  /** El valor más largo que guarda el caso, cuando se copia al caso (diseño M3 §1). */
  maxLength?: number;
}

/** Un grupo de campos, como lo nombra la fuente §7. */
export interface Grupo {
  titulo: string;
  campos: Campo[];
  /** Bajo el título: por qué nada actúa sobre estos campos, por ejemplo. */
  nota?: string;
}

/** Todos los campos del contrato v1, en su orden, agrupados como la fuente §7. */
export const GRUPOS: Grupo[] = [
  {
    titulo: 'Identificación',
    campos: [
      { clave: 'feria', etiqueta: 'Feria', forma: 'texto' },
      // DS_AVISO guarda 20 caracteres; la API rechaza uno más largo.
      {
        clave: 'aviso',
        etiqueta: 'Aviso',
        forma: 'texto',
        mono: true,
        maxLength: 20,
      },
      { clave: 'prioridad', etiqueta: 'Prioridad', forma: 'si-no' },
      {
        clave: 'fecha',
        etiqueta: 'Fecha (AAAA-MM-DD)',
        forma: 'texto',
        mono: true,
      },
    ],
  },
  {
    titulo: 'Reunión',
    campos: [
      { clave: 'responsable', etiqueta: 'Responsable', forma: 'texto' },
      { clave: 'acompanantes', etiqueta: 'Acompañantes', forma: 'texto' },
      {
        clave: 'reunion_con',
        etiqueta: 'Reunión con',
        forma: 'opcion',
        opciones: [
          ['cliente', 'Cliente'],
          ['otra persona', 'Otra persona'],
          ['', 'Sin marcar'],
        ],
      },
      { clave: 'relacion', etiqueta: 'Relación', forma: 'texto' },
    ],
  },
  {
    titulo: 'Cliente',
    campos: [
      { clave: 'cl', etiqueta: 'Código CL', forma: 'texto', mono: true },
      { clave: 'nombre', etiqueta: 'Nombre', forma: 'texto' },
      { clave: 'apellido1', etiqueta: 'Primer apellido', forma: 'texto' },
      { clave: 'apellido2', etiqueta: 'Segundo apellido', forma: 'texto' },
      { clave: 'movil', etiqueta: 'Móvil', forma: 'texto', mono: true },
      { clave: 'email', etiqueta: 'Email', forma: 'texto' },
      {
        clave: 'nacimiento',
        etiqueta: 'Nacimiento (como esté escrito)',
        forma: 'texto',
        mono: true,
      },
      {
        clave: 'cp_domicilio',
        etiqueta: 'CP del domicilio',
        forma: 'texto',
        mono: true,
      },
    ],
  },
  {
    titulo: 'Farmacia',
    campos: [
      { clave: 'farmacia_calle', etiqueta: 'Calle', forma: 'texto' },
      { clave: 'farmacia_poblacion', etiqueta: 'Población', forma: 'texto' },
      {
        clave: 'farmacia_cp',
        etiqueta: 'Código postal',
        forma: 'texto',
        mono: true,
      },
      {
        clave: 'farmacia_telefono',
        etiqueta: 'Teléfono',
        forma: 'texto',
        mono: true,
      },
    ],
  },
  {
    titulo: 'Relaciones',
    campos: [
      {
        clave: 'relacionadas',
        etiqueta: 'Personas o entidades relacionadas',
        forma: 'relaciones',
      },
    ],
  },
  {
    titulo: 'Observaciones',
    campos: [
      {
        clave: 'observaciones',
        etiqueta: 'Observaciones',
        forma: 'textoLargo',
      },
    ],
  },
  {
    titulo: 'Ficha de compra',
    campos: [
      {
        clave: 'facturacion',
        etiqueta: 'Tramos de facturación',
        forma: 'casillas',
        opciones: [
          '<150K',
          '150-300K',
          '300-500K',
          '500-700K',
          '700K-1M',
          '1-1,5M',
          '1,5-2M',
          '>2M',
        ].map((t) => [t, t]),
      },
      {
        clave: 'plazo',
        etiqueta: 'Plazo de compra',
        forma: 'opcion',
        opciones: [
          ['<1 año', 'Menos de 1 año'],
          ['1-2 años', '1 a 2 años'],
          ['>2 años', 'Más de 2 años'],
          ['Sin plazo', 'Sin plazo'],
          ['', 'Sin marcar'],
        ],
      },
      { clave: 'provincias', etiqueta: 'Provincias', forma: 'etiquetas' },
    ],
  },
  {
    titulo: 'Tratamiento',
    campos: [
      {
        clave: 'tratamiento',
        etiqueta: 'Tratamiento marcado',
        forma: 'casillas',
        opciones: [
          ['venta', 'Venta'],
          ['gestion', 'Gestión'],
          ['compra', 'Compra'],
          ['aviso_epc', 'Aviso EPC'],
          ['actividad_notificar', 'Actividad y notificación'],
        ],
      },
      {
        clave: 'nueva_ficha_epc',
        etiqueta: 'Generar nueva ficha EPC',
        forma: 'si-no',
      },
    ],
    nota: 'Solo informativo para EPC: el módulo no actúa sobre «Aviso EPC» ni «Generar nueva ficha EPC» (CA23).',
  },
  {
    titulo: 'Notas de tratamiento',
    campos: [
      {
        clave: 'notas_tratamiento',
        etiqueta: 'Notas para el tratamiento',
        forma: 'textoLargo',
      },
    ],
  },
];

/** Todos los campos, en el orden del contrato. */
export const CAMPOS: Campo[] = GRUPOS.flatMap((g) => g.campos);

/** El valor vacío de un campo, en su forma. */
export function valorVacio(campo: Campo): ValorCampo {
  switch (campo.forma) {
    case 'si-no':
      return false;
    case 'casillas':
    case 'etiquetas':
    case 'relaciones':
      return [];
    default:
      return '';
  }
}

/** Cómo se lee en pantalla el estado de lectura de un campo. */
export const ESTADO_LECTURA: Record<EstadoCampo, string> = {
  empty: 'Vacío',
  read: 'Leído',
  doubtful: 'Dudoso',
};

/** Si dos valores de un campo son el mismo, sea cual sea su forma. */
export function mismoValor(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
